import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Modal,
  Form,
  Alert,
  Tabs,
  Tab,
  ProgressBar,
  InputGroup
} from 'react-bootstrap';
import {
  FaPlus,
  FaEdit,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaGlobe,
  FaLock,
  FaUsers,
  FaDollarSign,
  FaTasks,
  FaRocket
} from 'react-icons/fa';
import axios from 'axios';

interface WhiteLabelClient {
  brokerage_id: number;
  company_name: string;
  contact_email: string;
  contact_phone: string;
  mls_id: string;
  desired_domain: string;
  domain_type: 'subdomain' | 'root';
  domain_verified: boolean;
  ssl_status: 'pending' | 'active' | 'error' | 'none';
  white_label_status: string;
  assigned_to: string;
  target_go_live_date: string;
  setup_fee_paid: boolean;
  white_label_fee: number;
  tasks_completed: number;
  total_tasks: number;
}

const WhiteLabelManagement: React.FC = () => {
  const [clients, setClients] = useState<WhiteLabelClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<WhiteLabelClient | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [availableBrokerages, setAvailableBrokerages] = useState<any[]>([]);
  const [quickSetupForm, setQuickSetupForm] = useState({
    brokerage_id: '',
    desired_domain: '',
    domain_type: 'subdomain',
    tagline: '',
    white_label_fee: 500,
    assigned_to: 'Michael',
    target_go_live_date: ''
  });
  const [stats, setStats] = useState({
    totalClients: 0,
    activeClients: 0,
    pendingSetup: 0,
    monthlyRevenue: 0,
    setupFeesCollected: 0
  });

  useEffect(() => {
    loadWhiteLabelClients();
    if (activeTab === 'quick-setup' || showAddModal) {
      loadAvailableBrokerages();
    }
  }, [activeTab, showAddModal]);

  const loadWhiteLabelClients = async () => {
    try {
      setLoading(true);
      
      // Fetch real data from API
      const response = await axios.get('/api/white-label/clients', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.data.success) {
        setClients(response.data.data);
        
        // Fetch stats
        const statsResponse = await axios.get('/api/white-label/stats', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        
        if (statsResponse.data.success) {
          const statsData = statsResponse.data.data;
          setStats({
            totalClients: statsData.total_clients,
            activeClients: statsData.active_clients,
            pendingSetup: statsData.pending_setup,
            monthlyRevenue: statsData.monthly_revenue,
            setupFeesCollected: statsData.setup_fees_collected
          });
        }
      } else {
        // Fallback to mock data if API fails
        const mockClients: WhiteLabelClient[] = [
          {
          brokerage_id: 1,
          company_name: 'Sunset Realty Group',
          contact_email: 'admin@sunsetrealty.com',
          contact_phone: '(555) 123-4567',
          mls_id: 'SUN01',
          desired_domain: 'homes.sunsetrealty.com',
          domain_type: 'subdomain',
          domain_verified: true,
          ssl_status: 'active',
          white_label_status: 'active',
          assigned_to: 'Implementation Team',
          target_go_live_date: '2024-01-15',
          setup_fee_paid: true,
          white_label_fee: 500,
          tasks_completed: 10,
          total_tasks: 10
        },
        {
          brokerage_id: 2,
          company_name: 'Premier Properties',
          contact_email: 'info@premierprops.com',
          contact_phone: '(555) 234-5678',
          mls_id: 'PRE01',
          desired_domain: 'search.premierproperties.com',
          domain_type: 'subdomain',
          domain_verified: false,
          ssl_status: 'pending',
          white_label_status: 'dns_pending',
          assigned_to: 'Michael',
          target_go_live_date: '2024-01-20',
          setup_fee_paid: true,
          white_label_fee: 500,
          tasks_completed: 5,
          total_tasks: 10
        }
      ];

      setClients(mockClients);
      
      // Calculate stats
      setStats({
        totalClients: mockClients.length,
        activeClients: mockClients.filter(c => c.white_label_status === 'active').length,
        pendingSetup: mockClients.filter(c => ['setup_in_progress', 'dns_pending', 'testing'].includes(c.white_label_status)).length,
        monthlyRevenue: mockClients.filter(c => c.white_label_status === 'active').reduce((sum, c) => sum + c.white_label_fee, 0),
        setupFeesCollected: mockClients.filter(c => c.setup_fee_paid).length * 500
      });
      }
    } catch (error) {
      console.error('Failed to load white-label clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableBrokerages = async () => {
    try {
      const response = await axios.get('/api/white-label/available-brokerages', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.data.success) {
        setAvailableBrokerages(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load available brokerages:', error);
    }
  };

  const handleCheckDNS = async () => {
    if (!quickSetupForm.desired_domain) {
      alert('Please enter a domain to check');
      return;
    }

    try {
      // Check if domain is already in use
      const inUse = clients.some(client => 
        client.desired_domain?.toLowerCase() === quickSetupForm.desired_domain.toLowerCase()
      );

      if (inUse) {
        alert(`❌ Domain "${quickSetupForm.desired_domain}" is already in use by another client`);
        return;
      }

      // Basic DNS validation
      const domain = quickSetupForm.desired_domain.toLowerCase();
      const domainRegex = /^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}$/;

      if (!domainRegex.test(domain)) {
        alert('❌ Invalid domain format. Please enter a valid domain (e.g., homes.brokername.com)');
        return;
      }

      // For subdomains, check if it starts with a subdomain
      if (quickSetupForm.domain_type === 'subdomain' && !domain.includes('.') ) {
        alert('❌ Subdomain should be in format: subdomain.domain.com');
        return;
      }

      alert(`✅ Domain "${quickSetupForm.desired_domain}" is available and valid!\n\nDNS configuration will be required after setup.`);
    } catch (error) {
      console.error('DNS check error:', error);
      alert('Failed to check DNS');
    }
  };

  const handleSaveAsDraft = async () => {
    if (!quickSetupForm.brokerage_id) {
      alert('Please select a brokerage before saving as draft');
      return;
    }

    try {
      // Save to localStorage for now
      const drafts = JSON.parse(localStorage.getItem('white_label_drafts') || '[]');
      const draft = {
        ...quickSetupForm,
        savedAt: new Date().toISOString(),
        id: Date.now()
      };
      
      drafts.push(draft);
      localStorage.setItem('white_label_drafts', JSON.stringify(drafts));
      
      alert('✅ Draft saved successfully! You can continue editing later.');
    } catch (error) {
      console.error('Save draft error:', error);
      alert('Failed to save draft');
    }
  };

  const handleQuickSetupSubmit = async () => {
    try {
      if (!quickSetupForm.brokerage_id || !quickSetupForm.desired_domain) {
        alert('Please select a brokerage and enter a domain');
        return;
      }
      
      const response = await axios.post('/api/white-label/create-extension', quickSetupForm, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.data.success) {
        alert(`White-label setup created for ${response.data.data.brokerage_name}!`);
        // Reset form
        setQuickSetupForm({
          brokerage_id: '',
          desired_domain: '',
          domain_type: 'subdomain',
          tagline: '',
          white_label_fee: 500,
          assigned_to: 'Michael',
          target_go_live_date: ''
        });
        // Reload clients
        loadWhiteLabelClients();
        setActiveTab('overview');
      } else {
        alert('Error: ' + response.data.error);
      }
    } catch (error: any) {
      alert('Failed to create white-label setup: ' + (error.response?.data?.error || error.message));
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { variant: string; icon: JSX.Element } } = {
      'active': { variant: 'success', icon: <FaCheckCircle /> },
      'dns_pending': { variant: 'warning', icon: <FaGlobe /> },
      'setup_in_progress': { variant: 'info', icon: <FaClock /> },
      'testing': { variant: 'primary', icon: <FaTasks /> },
      'suspended': { variant: 'danger', icon: <FaTimesCircle /> }
    };

    const config = statusConfig[status] || { variant: 'secondary', icon: <FaClock /> };
    
    return (
      <Badge bg={config.variant} className="d-flex align-items-center gap-1">
        {config.icon} {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const getSslBadge = (status: string) => {
    const sslConfig: { [key: string]: { variant: string; text: string } } = {
      'active': { variant: 'success', text: 'SSL Active' },
      'pending': { variant: 'warning', text: 'SSL Pending' },
      'error': { variant: 'danger', text: 'SSL Error' },
      'none': { variant: 'secondary', text: 'No SSL' }
    };

    const config = sslConfig[status] || sslConfig['none'];
    
    return <Badge bg={config.variant}><FaLock /> {config.text}</Badge>;
  };

  return (
    <Container fluid className="white-label-management p-0">
      {/* Header Stats */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Row>
                <Col md={3}>
                  <div className="text-center">
                    <h3>{stats.totalClients}</h3>
                    <p className="text-muted mb-0">Total Clients</p>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <h3 className="text-success">{stats.activeClients}</h3>
                    <p className="text-muted mb-0">Active Sites</p>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <h3 className="text-warning">{stats.pendingSetup}</h3>
                    <p className="text-muted mb-0">In Progress</p>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <h3 className="text-primary">${stats.monthlyRevenue}</h3>
                    <p className="text-muted mb-0">Monthly Revenue</p>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Main Content Tabs */}
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'overview')} className="mb-3">
        <Tab eventKey="overview" title="Client Overview">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">White-Label Clients</h5>
              <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
                <FaPlus /> Add New Client
              </Button>
            </Card.Header>
            <Card.Body>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Domain</th>
                    <th>Status</th>
                    <th>SSL</th>
                    <th>Progress</th>
                    <th>Go-Live Date</th>
                    <th>Monthly Fee</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client) => (
                    <tr key={client.brokerage_id}>
                      <td>
                        <strong>{client.company_name}</strong>
                        <br />
                        <small className="text-muted">{client.contact_email}</small>
                      </td>
                      <td>
                        <code>{client.desired_domain}</code>
                        <br />
                        {client.domain_verified ? 
                          <Badge bg="success" className="badge-sm"><FaCheckCircle /> Verified</Badge> : 
                          <Badge bg="warning" className="badge-sm"><FaClock /> Pending</Badge>
                        }
                      </td>
                      <td>{getStatusBadge(client.white_label_status)}</td>
                      <td>{getSslBadge(client.ssl_status)}</td>
                      <td>
                        <ProgressBar 
                          now={(client.tasks_completed / client.total_tasks) * 100} 
                          label={`${client.tasks_completed}/${client.total_tasks}`}
                          variant={client.tasks_completed === client.total_tasks ? 'success' : 'primary'}
                        />
                      </td>
                      <td>{new Date(client.target_go_live_date).toLocaleDateString()}</td>
                      <td>${client.white_label_fee}/mo</td>
                      <td>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => setSelectedClient(client)}
                        >
                          <FaEdit /> Manage
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="pipeline" title="Implementation Pipeline">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Implementation Pipeline</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                {['contract_pending', 'payment_pending', 'setup_in_progress', 'dns_pending', 'testing'].map((stage) => (
                  <Col key={stage} md={2}>
                    <Card className="mb-3">
                      <Card.Header className="bg-light">
                        <h6 className="mb-0">{stage.replace(/_/g, ' ').toUpperCase()}</h6>
                      </Card.Header>
                      <Card.Body>
                        {clients.filter(c => c.white_label_status === stage).map(client => (
                          <div key={client.brokerage_id} className="mb-2 p-2 border rounded">
                            <strong className="d-block">{client.company_name}</strong>
                            <small className="text-muted">{client.assigned_to}</small>
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="quick-setup" title="Quick Setup">
          <Card>
            <Card.Header>
              <h5 className="mb-0"><FaRocket /> Manual White-Label Setup</h5>
            </Card.Header>
            <Card.Body>
              <Alert variant="info">
                <strong>Quick Setup Mode:</strong> Add white-label configuration to existing brokerages
              </Alert>
              
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Select Existing Brokerage</Form.Label>
                      <Form.Select 
                        value={quickSetupForm.brokerage_id}
                        onChange={(e) => setQuickSetupForm({...quickSetupForm, brokerage_id: e.target.value})}
                      >
                        <option value="">Choose a brokerage...</option>
                        {availableBrokerages.map(brokerage => (
                          <option key={brokerage.id} value={brokerage.id}>
                            {brokerage.name} {brokerage.office_mls_id ? `(MLS: ${brokerage.office_mls_id})` : ''}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Desired Domain</Form.Label>
                      <InputGroup>
                        <Form.Control 
                          placeholder="homes.theirbrokerage.com" 
                          value={quickSetupForm.desired_domain}
                          onChange={(e) => setQuickSetupForm({...quickSetupForm, desired_domain: e.target.value})}
                        />
                        <Button variant="outline-secondary" onClick={handleCheckDNS}>Check DNS</Button>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Domain Type</Form.Label>
                      <Form.Select
                        value={quickSetupForm.domain_type}
                        onChange={(e) => setQuickSetupForm({...quickSetupForm, domain_type: e.target.value})}
                      >
                        <option value="subdomain">Subdomain</option>
                        <option value="root">Root Domain</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Monthly Fee</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control type="number" defaultValue="500" />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Target Go-Live</Form.Label>
                      <Form.Control type="date" />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Implementation Notes</Form.Label>
                      <Form.Control as="textarea" rows={3} placeholder="Special requirements, DNS info, etc." />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end gap-2">
                  <Button variant="secondary" onClick={handleSaveAsDraft}>Save as Draft</Button>
                  <Button variant="primary" onClick={handleQuickSetupSubmit}>
                    <FaPlus /> Create White-Label Setup
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="templates" title="Email Templates">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Client Communication Templates</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <h6>Welcome Email</h6>
                  <Button variant="outline-primary" size="sm" className="w-100 mb-3">
                    Copy Template
                  </Button>
                </Col>
                <Col md={4}>
                  <h6>DNS Instructions</h6>
                  <Button variant="outline-primary" size="sm" className="w-100 mb-3">
                    Copy Template
                  </Button>
                </Col>
                <Col md={4}>
                  <h6>Go-Live Announcement</h6>
                  <Button variant="outline-primary" size="sm" className="w-100 mb-3">
                    Copy Template
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Client Detail Modal */}
      <Modal show={selectedClient !== null} onHide={() => setSelectedClient(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedClient?.company_name} - White-Label Management</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedClient && (
            <>
              <h6>Setup Progress</h6>
              <ProgressBar 
                now={(selectedClient.tasks_completed / selectedClient.total_tasks) * 100} 
                label={`${selectedClient.tasks_completed} of ${selectedClient.total_tasks} tasks completed`}
                className="mb-4"
              />
              
              <h6>Quick Actions</h6>
              <div className="d-flex gap-2 mb-4">
                <Button variant="outline-primary" size="sm">Send DNS Instructions</Button>
                <Button variant="outline-success" size="sm">Test Domain</Button>
                <Button variant="outline-warning" size="sm">Generate SSL</Button>
                <Button variant="outline-info" size="sm">Preview Site</Button>
              </div>

              <h6>Contact Information</h6>
              <p>
                Email: {selectedClient.contact_email}<br />
                Phone: {selectedClient.contact_phone}<br />
                MLS ID: {selectedClient.mls_id}
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedClient(null)}>Close</Button>
          <Button variant="primary">Save Changes</Button>
        </Modal.Footer>
      </Modal>

      {/* Add New Client Modal */}
      <Modal 
        show={showAddModal} 
        onHide={() => {
          setShowAddModal(false);
          setQuickSetupForm({
            brokerage_id: '',
            desired_domain: '',
            domain_type: 'subdomain',
            tagline: '',
            white_label_fee: 500,
            assigned_to: 'Michael',
            target_go_live_date: ''
          });
        }}
        size="lg"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaPlus className="text-primary" />
            Add New White-Label Client
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          <Alert variant="info" className="mb-4">
            <strong>White-Label Platform Setup</strong>
            <p className="mb-0 mt-2">
              Configure a custom-branded property search website for brokerages and agents:
            </p>
            <ul className="mb-0 mt-2">
              <li>Custom domain (e.g., homes.brokername.com)</li>
              <li>Branded property search interface</li>
              <li>Integrated AI-powered search</li>
              <li>Lead capture and CRM integration</li>
            </ul>
          </Alert>

          <Form>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Select Brokerage</Form.Label>
                  <Form.Select 
                    value={quickSetupForm.brokerage_id}
                    onChange={(e) => setQuickSetupForm({...quickSetupForm, brokerage_id: e.target.value})}
                    className="form-select-lg"
                  >
                    <option value="">Choose a brokerage...</option>
                    {availableBrokerages.map(brokerage => (
                      <option key={brokerage.id} value={brokerage.id}>
                        {brokerage.name} {brokerage.office_mls_id ? `(MLS: ${brokerage.office_mls_id})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                  {availableBrokerages.length === 0 && (
                    <Form.Text className="text-muted">
                      No brokerages available. All existing brokerages may already have white-label setups.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Desired Domain</Form.Label>
                  <InputGroup>
                    <Form.Control 
                      placeholder="homes.brokername.com" 
                      value={quickSetupForm.desired_domain}
                      onChange={(e) => setQuickSetupForm({...quickSetupForm, desired_domain: e.target.value})}
                    />
                    <Button variant="outline-secondary" onClick={handleCheckDNS}>
                      <FaGlobe /> Check DNS
                    </Button>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Enter the custom domain for the white-label website
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Domain Type</Form.Label>
                  <Form.Select
                    value={quickSetupForm.domain_type}
                    onChange={(e) => setQuickSetupForm({...quickSetupForm, domain_type: e.target.value})}
                  >
                    <option value="subdomain">Subdomain</option>
                    <option value="root">Root Domain</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Tagline (Optional)</Form.Label>
                  <Form.Control 
                    placeholder="Your trusted real estate partner" 
                    value={quickSetupForm.tagline}
                    onChange={(e) => setQuickSetupForm({...quickSetupForm, tagline: e.target.value})}
                  />
                  <Form.Text className="text-muted">
                    Appears on the branded website header
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Monthly Fee</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control 
                      type="number" 
                      value={quickSetupForm.white_label_fee}
                      onChange={(e) => setQuickSetupForm({...quickSetupForm, white_label_fee: parseInt(e.target.value) || 500})}
                    />
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Standard fee: $500/month
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Assigned To</Form.Label>
                  <Form.Control 
                    value={quickSetupForm.assigned_to}
                    onChange={(e) => setQuickSetupForm({...quickSetupForm, assigned_to: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Target Go-Live Date</Form.Label>
                  <Form.Control 
                    type="date" 
                    value={quickSetupForm.target_go_live_date}
                    onChange={(e) => setQuickSetupForm({...quickSetupForm, target_go_live_date: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer className="bg-light">
          <Button 
            variant="secondary" 
            onClick={handleSaveAsDraft}
          >
            <FaClock /> Save as Draft
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              handleQuickSetupSubmit();
              setShowAddModal(false);
            }}
            disabled={!quickSetupForm.brokerage_id || !quickSetupForm.desired_domain}
          >
            <FaPlus /> Create White-Label Setup
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default WhiteLabelManagement;